import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

/**
 * useTicketAdmin — avgör om användaren får hantera ärenden.
 *
 * Speglar backendens behörighetsgräns exakt: RLS på tickets/ticket_messages
 * släpper igenom is_superadmin() eller has_admin_role(), där has_admin_role
 * omfattar customer_admin, partner_admin, hq_admin och superadmin (aktiva,
 * ej utgångna rader i user_role_assignments). Superadmin verifieras även
 * via RPC:n is_superadmin som fallback — samma mönster som api-tickets —
 * ifall roll-raden inte är läsbar för klienten.
 *
 * OBS: Detta är enbart en UX-gate (visa/dölj ärendehanterings-UI och
 * redirect:a icke-admins). RLS är den verkliga behörighetsgränsen —
 * en org-admin ser bara sin organisations ärenden oavsett vad frontenden
 * frågar efter (CLAUDE.md §5.4).
 */

/** Roller som has_admin_role() godkänner — håll i synk med RLS-helpern. */
const TICKET_ADMIN_ROLES = ['superadmin', 'customer_admin', 'partner_admin', 'hq_admin'];

interface TicketAdminAccess {
  isSuperadmin: boolean;
  isOrgAdmin: boolean;
  organizationId: string | null;
}

export interface TicketAdminData {
  isSuperadmin: boolean;
  /** Någon ärendeadmin-roll — får hantera ärenden. */
  isTicketAdmin: boolean;
  /** Org-admins organisation (defense-in-depth-filter i queries). */
  organizationId: string | null;
  isLoading: boolean;
}

export function useTicketAdmin(): TicketAdminData {
  const { user, loading: authLoading } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['ticket-admin-role', user?.id],
    queryFn: async (): Promise<TicketAdminAccess> => {
      const { data, error } = await supabase
        .from('user_role_assignments')
        .select('role, organization_id, expires_at')
        .eq('user_id', user!.id)
        .eq('is_active', true)
        .in('role', TICKET_ADMIN_ROLES);

      if (error) throw error;

      const now = Date.now();
      const active = (data ?? []).filter(
        (r) => !r.expires_at || new Date(r.expires_at).getTime() > now
      );

      let isSuperadmin = active.some((r) => r.role === 'superadmin');

      // Fallback: superadmin-status kan finnas utan läsbar roll-rad —
      // fråga samma RPC som backenden/RLS använder (api-tickets-mönstret).
      if (!isSuperadmin && active.length === 0) {
        const { data: rpcResult } = await supabase.rpc('is_superadmin', {
          _user_id: user!.id,
        });
        isSuperadmin = rpcResult === true;
      }

      const orgAdminRoles = active.filter((r) => r.role !== 'superadmin');

      return {
        isSuperadmin,
        isOrgAdmin: orgAdminRoles.length > 0,
        organizationId: orgAdminRoles.find((r) => r.organization_id)?.organization_id ?? null,
      };
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  return {
    isSuperadmin: data?.isSuperadmin ?? false,
    isTicketAdmin: (data?.isSuperadmin || data?.isOrgAdmin) ?? false,
    organizationId: data?.organizationId ?? null,
    isLoading: authLoading || (!!user && isLoading),
  };
}
